import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  DropzoneWrapper,
  DropzoneText,
  DropzoneAdditionalText,
  CameraIconStyled,
  UploadIconStyled,
} from './FileDropzone.styled';
import { useTheme } from '../ThemeProvider';
import { isMobile } from 'react-device-detect';
import UploadedFile from './components/UploadedFile/UploadedFile';
import { Button } from '../Button/Button';
import { ButtonVariantsEnum } from '../Button/Button.types';

type DisplayText = JSX.Element | string | React.ReactNode;
export type SVGComponent = React.FunctionComponent<
  React.SVGAttributes<SVGElement>
>;
interface IconSet {
  UploadIcon?: SVGComponent;
  CameraIcon?: SVGComponent;
  FileIcon?: SVGComponent;
}

export type FileDropzoneProps = {
  maxFiles: number;
  desktopText: DisplayText;
  mobileText: DisplayText;
  additionalText?: DisplayText;
  buttonText?: DisplayText;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  maxSize: number;
} & IconSet;

const prepareProgressPercent = (event: ProgressEvent<FileReader>): number =>
  Number(((event.loaded / event.total) * 100).toFixed(2));

export const FileDropzone = ({
  maxFiles,
  desktopText,
  mobileText,
  additionalText,
  files,
  buttonText,
  setFiles,
  maxSize,
  UploadIcon = UploadIconStyled,
  CameraIcon = CameraIconStyled,
  FileIcon,
}: FileDropzoneProps): JSX.Element => {
  const theme = useTheme();
  const [progress, setProgress] = useState<number>(0);

  const handleRemoveFile = (fileToRemove: File): void => {
    setFiles((currentFiles: File[]) =>
      currentFiles.filter((file) => file !== fileToRemove),
    );
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setFiles((prevFiles: File[]): File[] => [...prevFiles, ...acceptedFiles]);

      acceptedFiles.forEach((file: File) => {
        const reader = new FileReader();

        reader.onprogress = (event) => {
          setProgress(prepareProgressPercent(event));
        };
        reader.onloadend = (event) => {
          setProgress(prepareProgressPercent(event));
        };
        reader.readAsDataURL(file);
      });
    },
    [setFiles],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: maxFiles,
    preventDropOnDocument: false,
    disabled: files.length >= maxFiles,
    maxSize: maxSize,
  });

  const acceptedFileItems = files.map((file: File, index: number) => (
    <UploadedFile
      file={file}
      index={index}
      handleRemoveFile={handleRemoveFile}
      progress={progress}
      key={index}
      Icon={FileIcon}
    />
  ));

  const renderedIcon = (isMobile ? <CameraIcon /> : <UploadIcon />) ?? null;

  return (
    <section className="container">
      {files.length < maxFiles && (
        <DropzoneWrapper {...getRootProps()} data-testid="dropzone">
          <input {...getInputProps()} data-testid="dropzone-input" />
          {renderedIcon}
          <DropzoneText
            variant="subtitle1"
            color={theme.palette.text.paragraph.primary}
          >
            {isMobile ? mobileText : desktopText}
          </DropzoneText>
          {buttonText && (
            <Button
              typeStyle={ButtonVariantsEnum.Secondary}
              size="small"
              style={{ pointerEvents: 'none' }}
            >
              {buttonText}
            </Button>
          )}
          {additionalText && (
            <DropzoneAdditionalText
              variant="text1"
              color={theme.palette.text.paragraph.secondary}
            >
              {additionalText}
            </DropzoneAdditionalText>
          )}
        </DropzoneWrapper>
      )}
      <aside>{files.length > 0 && <>{acceptedFileItems}</>}</aside>
    </section>
  );
};
